import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Slider } from '@mui/material';
import { useSearch } from '@tanstack/react-router';

import { useAspenLogger } from 'hooks/useAspenLogger';

import styles from './seeker.module.scss';

interface SeekerProps {
  elapsed: number;
  duration: number;
  disabled?: boolean;
  handleOnSeek: (event: Event, newValue: number | number[]) => void;
  isPlaying?: boolean;
  onSeekingChange: (isSeeking: boolean) => void;
  loading?: boolean;
}

export const Seeker = ({
  elapsed,
  duration,
  disabled = false,
  handleOnSeek,
  isPlaying = false,
  onSeekingChange,
  loading = false,
}: SeekerProps) => {
  const { eventId } = useSearch({ from: '/events' });
  const { logAspenEvent } = useAspenLogger();

  const [animatedElapsed, setAnimatedElapsed] = useState(elapsed);
  const [isSeeking, setIsSeeking] = useState(false);
  const animationFrameRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);

  // Animation loop for smooth updates when playing
  const animate = useCallback(() => {
    if (isPlaying && !isSeeking && duration > 0 && !disabled) {
      const now = performance.now();

      if (lastUpdateRef.current > 0) {
        // Simple interpolation - add elapsed time since last frame
        const deltaTime = (now - lastUpdateRef.current) / 1000;
        setAnimatedElapsed((prev) => Math.min(prev + deltaTime, duration));
      }
      lastUpdateRef.current = now;

      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [isPlaying, isSeeking, duration, disabled]);

  // Reset elapsed time when loading starts (video switch)
  useEffect(() => {
    if (loading) {
      setAnimatedElapsed(0);
    }
  }, [loading]);

  // Handle animation and syncing
  useEffect(() => {
    // Sync with parent elapsed when not seeking and playing (avoid pause jumps)
    if (!isSeeking && isPlaying && !disabled) {
      setAnimatedElapsed(elapsed);
    }

    // Start/stop animation based on playing state
    if (isPlaying && !isSeeking && !disabled) {
      lastUpdateRef.current = 0;
      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, isSeeking, animate, elapsed, disabled]);

  const handleSeek = useCallback(
    (event: Event, newValue: number | number[]) => {
      if (disabled) return;
      setAnimatedElapsed(newValue as number);
      handleOnSeek(event, newValue);
    },
    [handleOnSeek, disabled],
  );

  return (
    <Slider
      aria-label="Seeker"
      value={animatedElapsed}
      min={0}
      max={duration}
      step={duration / 100}
      disabled={disabled}
      onChange={handleSeek}
      onChangeCommitted={(_event, _newValue) => {
        if (disabled) return;
        setIsSeeking(false);
        onSeekingChange?.(false);
        if (eventId) {
          logAspenEvent({
            log_id: 'iot.web.event.interaction',
            payload: {
              event_id: eventId,
              interaction: 'seek',
            },
          });
        }
      }}
      onMouseDown={() => {
        if (disabled) return;
        setIsSeeking(true);
        onSeekingChange?.(true);
      }}
      onTouchStart={() => {
        if (disabled) return;
        setIsSeeking(true);
        onSeekingChange?.(true);
      }}
      className={styles.slider}
      sx={{
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      }}
    />
  );
};
